(() => {
  "use strict";

  if (window.ElookusMultiPairCartSync) return;

  const PROPERTY = "_elookus_multipair_discount_qty";
  const BASE_PROPERTY = "_elookus_multipair_discount_base";
  const FRAME_CENTS_PROPERTY = "_elookus_frame_unit_price";
  const FRAME_DOLLARS_PROPERTY = "_elookus_frame_discount_price";
  const ELIGIBLE_TAG = "MULTI_PAIR_ELIGIBLE";
  const MIN_FRAME_DOLLARS = 10;
  const SUNGLASSES_TYPE = "sunglasses";
  const STABILIZE_DELAYS = [80, 120, 180, 260, 380, 560, 800, 1100, 1500, 2000];
  const EXCLUDED_TAGS = new Set([
    "CLEARANCE",
    "FINAL_SALE",
    "MULTI_PAIR_EXCLUDED",
    "MULTI_PAIR_INELIGIBLE",
    "NO_MULTI_PAIR",
  ]);
  const state = {
    productData: new Map(),
    promise: null,
    timer: 0,
    queuedCart: null,
    dispatching: false,
  };

  const isCart = (cart) => Boolean(cart && Array.isArray(cart.items));
  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  function entries(item) {
    if (Array.isArray(item?.properties)) {
      return item.properties.map((entry) => [String(entry?.name || ""), entry?.value]);
    }
    return Object.entries(item?.properties || {});
  }

  function property(item, key) {
    const match = entries(item).find(([name]) => name === key);
    return match ? String(match[1] ?? "").trim() : "";
  }

  function propertiesObject(item) {
    return Object.fromEntries(entries(item).filter(([key]) => key));
  }

  function isElookusProduct(item) {
    return /^elookus$/i.test(String(item?.vendor || "").trim());
  }

  function productHandle(item) {
    if (item?.handle) return String(item.handle);
    return String(item?.url || "").match(/\/products\/([^/?#]+)/)?.[1] || "";
  }

  function normalizedTags(product) {
    const raw = Array.isArray(product?.tags)
      ? product.tags
      : String(product?.tags || "").split(",");
    return raw
      .map((tag) => String(tag).trim().toUpperCase().replace(/[\s-]+/g, "_"))
      .filter(Boolean);
  }

  async function loadProductData(item) {
    const handle = productHandle(item);
    if (!handle) return null;
    if (state.productData.has(handle)) return state.productData.get(handle);

    const request = window.fetch(
      `${window.Shopify?.routes?.root || "/"}products/${encodeURIComponent(handle)}.js`,
      {
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      }
    ).then((response) => {
      if (!response.ok) {
        throw new Error(`Product eligibility request failed (${response.status})`);
      }
      return response.json();
    }).then((product) => {
      const tags = normalizedTags(product);
      const excluded = tags.some((tag) => EXCLUDED_TAGS.has(tag));
      return {
        eligible: !excluded && tags.includes(ELIGIBLE_TAG),
        productType: String(product?.type || "").trim().toLowerCase(),
        variants: Array.isArray(product?.variants) ? product.variants : [],
      };
    }).catch((error) => {
      console.warn(`[ELOOKUS Multi-Pair] ${error.message}`);
      return null;
    });

    state.productData.set(handle, request);
    const data = await request;
    state.productData.set(handle, data);
    return data;
  }

  function variantBaseCents(item, data) {
    const variantId = String(item?.variant_id || item?.id || "");
    if (!variantId || !data?.variants?.length) return null;
    const variant = data.variants.find(
      (candidate) => String(candidate?.id || "") === variantId
    );
    const price = Number(variant?.price);
    return Number.isFinite(price) && price >= 0 ? Math.round(price) : null;
  }

  function storedFrameCents(item) {
    const centsValue = property(item, FRAME_CENTS_PROPERTY);
    if (centsValue !== "") {
      const cents = Number(centsValue);
      if (Number.isFinite(cents) && cents >= 0) return Math.round(cents);
    }

    const dollarsValue = property(item, FRAME_DOLLARS_PROPERTY);
    if (dollarsValue !== "") {
      const dollars = Number(dollarsValue);
      if (Number.isFinite(dollars) && dollars >= 0) {
        return Math.round(dollars * 100);
      }
    }

    return null;
  }

  async function frameLine(item, index) {
    if (!isElookusProduct(item)) return null;

    const data = await loadProductData(item);
    if (!data) return null;

    let frameCents = storedFrameCents(item);

    // Prescription items carry the authoritative frame-only price from the ELOOKUS lens flow.
    // Only plain Sunglasses may fall back to the selected Shopify variant price.
    if (frameCents === null && data.productType === SUNGLASSES_TYPE) {
      frameCents = variantBaseCents(item, data);
    }

    if (!Number.isFinite(frameCents) || frameCents < 0) return null;

    const quantity = Math.max(0, Math.floor(Number(item.quantity) || 0));
    const frameDollars = frameCents / 100;
    const eligible = frameDollars >= MIN_FRAME_DOLLARS && data.eligible;

    return {
      item,
      index,
      quantity,
      frameCents,
      frameDollars,
      eligible,
      discountQuantity: 0,
      discountBase: "0.00",
    };
  }

  async function plan(cart) {
    const frames = await Promise.all(
      (cart.items || []).map((item, index) => frameLine(item, index))
    );
    const elookusFrames = frames.filter(Boolean);
    const eligibleFrames = elookusFrames
      .filter((line) => line.eligible)
      .sort(
        (left, right) =>
          left.frameDollars - right.frameDollars || left.index - right.index
      );

    let remaining = Math.floor(
      eligibleFrames.reduce((sum, line) => sum + line.quantity, 0) / 2
    );

    eligibleFrames.forEach((line) => {
      line.discountQuantity = Math.min(line.quantity, remaining);
      line.discountBase = (line.discountQuantity * line.frameDollars).toFixed(2);
      remaining -= line.discountQuantity;
    });

    return elookusFrames;
  }

  async function fetchCart() {
    const response = await window.fetch("/cart.js", {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  async function changeLine(line) {
    const properties = propertiesObject(line.item);
    properties[FRAME_CENTS_PROPERTY] = String(line.frameCents);
    properties[FRAME_DOLLARS_PROPERTY] = line.frameDollars.toFixed(2);
    properties[PROPERTY] = String(line.discountQuantity);
    properties[BASE_PROPERTY] = line.discountBase;

    const response = await window.fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({
        id: line.item.key,
        quantity: line.quantity,
        properties,
      }),
    });
    if (!response.ok) {
      throw new Error(`Multi-Pair property update failed (${response.status})`);
    }
    return response.json();
  }

  function lineNeedsUpdate(line) {
    return property(line.item, FRAME_CENTS_PROPERTY) !== String(line.frameCents)
      || property(line.item, FRAME_DOLLARS_PROPERTY) !== line.frameDollars.toFixed(2)
      || property(line.item, PROPERTY) !== String(line.discountQuantity)
      || property(line.item, BASE_PROPERTY) !== line.discountBase;
  }

  function isMultiPairDiscountTitle(value) {
    const title = String(value || "").trim().toLowerCase();
    return title.includes("multi-pair")
      || title.includes("multi pair")
      || title.includes("every 2nd frame");
  }

  function actualMultiPairSavings(cart) {
    return (cart?.items || []).reduce((cartTotal, item) => {
      const allocations = Array.isArray(item?.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : [];
      return cartTotal + allocations.reduce((lineTotal, allocation) => {
        const title = allocation?.discount_application?.title || allocation?.title || "";
        return isMultiPairDiscountTitle(title)
          ? lineTotal + (Number(allocation?.amount) || 0)
          : lineTotal;
      }, 0);
    }, 0);
  }

  function expectedMultiPairSavings(cart) {
    return (cart?.items || []).reduce((total, item) => {
      const frameCents = storedFrameCents(item);
      const quantity = Math.max(0, Math.floor(Number(item?.quantity) || 0));
      const discountQuantity = Math.max(
        0,
        Math.min(quantity, Math.floor(Number(property(item, PROPERTY)) || 0))
      );
      if (!Number.isFinite(frameCents) || frameCents < 0 || discountQuantity <= 0) return total;
      return total + (Math.round(frameCents / 2) * discountQuantity);
    }, 0);
  }

  async function waitForDiscountState(cart) {
    let latest = cart;
    const initialActual = actualMultiPairSavings(latest);

    for (let index = 0; index <= STABILIZE_DELAYS.length; index += 1) {
      const expected = expectedMultiPairSavings(latest);
      const actual = actualMultiPairSavings(latest);
      if (Math.abs(actual - expected) <= 1) {
        return {
          cart: latest,
          settled: true,
          changed: actual !== initialActual,
        };
      }
      if (index === STABILIZE_DELAYS.length) break;
      await delay(STABILIZE_DELAYS[index]);
      latest = await fetchCart();
    }

    return {
      cart: latest,
      settled: false,
      changed: actualMultiPairSavings(latest) !== initialActual,
    };
  }

  async function run(suppliedCart) {
    let cart = isCart(suppliedCart) ? suppliedCart : await fetchCart();
    let changed = false;
    const limit = Math.max(8, (cart.items?.length || 0) * 4);

    for (let attempt = 0; attempt < limit; attempt += 1) {
      const allocation = await plan(cart);
      const mismatch = allocation.find(lineNeedsUpdate);
      if (!mismatch) {
        const settled = await waitForDiscountState(cart);
        return {
          cart: settled.cart,
          changed: changed || settled.changed,
          settled: settled.settled,
        };
      }
      cart = await changeLine(mismatch);
      changed = true;
    }

    throw new Error("Multi-Pair allocation did not stabilize");
  }

  async function sync(suppliedCart) {
    if (state.promise) {
      await state.promise;
      return sync();
    }

    state.promise = run(suppliedCart);
    try {
      return await state.promise;
    } finally {
      state.promise = null;
    }
  }

  function schedule(suppliedCart) {
    if (isCart(suppliedCart)) state.queuedCart = suppliedCart;
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(async () => {
      state.timer = 0;
      const queued = state.queuedCart;
      state.queuedCart = null;
      try {
        const result = await sync(queued);
        if (result.changed) {
          state.dispatching = true;
          document.dispatchEvent(
            new CustomEvent("on:cart:change", {
              bubbles: true,
              cancelable: false,
            })
          );
          state.dispatching = false;
        }
        if (!result.settled) {
          console.warn("[ELOOKUS Multi-Pair] Discount amount is still updating; another reconciliation was scheduled.");
          schedule();
        }
      } catch (error) {
        console.warn(`[ELOOKUS Multi-Pair] ${error.message}`);
      }
    }, 60);
  }

  function handleCartEvent(event) {
    if (state.dispatching) return;
    const cart = event.detail?.cart || (isCart(event.detail) ? event.detail : null);
    schedule(cart);
  }

  [
    "on:cart:add",
    "on:cart:change",
    "cart:updated",
    "cart:refresh",
    "theme:cart:updated",
  ].forEach((name) => document.addEventListener(name, handleCartEvent));

  window.ElookusMultiPairCartSync = Object.freeze({ sync, schedule });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => schedule(), { once: true });
  } else {
    schedule();
  }
})();
